<template>
  <Card id="bovine-card" :style="cardStyle">
    <template v-slot:content>
      <div
        class="grid-container"
        :class="{ 'grid-tags': !isTypeBovineRoot(bovine.typeId) }"
      >
        <div class="id-brinco">
          <BovineTag :bovine="bovine" :inactive="this.isInactive"></BovineTag>
        </div>
        <div class="breed">
          <span class="bovine-card-field">
            <span>
              <IconBreed style="margin-top: 5px" />
            </span>
            <p>
              {{ bovine?.breed?.name || '-' }}
            </p>
          </span>
        </div>
        <div class="weight">
          <span class="bovine-card-field">
            <span>
              <IconWeight style="margin-top: 5px" />
            </span>
            <p>
              {{ bovine.weight ? $toDecimal(bovine.weight) : '-' }}
            </p>
          </span>
        </div>
        <div class="birth-date">
          <span class="bovine-card-field">
            <span class="material-icons">event</span>
            <p>
              {{
                bovine.birthDate
                  ? $dayjs(bovine.birthDate).format('DD/MM/YYYY')
                  : '-'
              }}
            </p>
          </span>
        </div>
        <div class="age">
          <span class="bovine-card-field">
            <span class="material-icons">hourglass_empty</span>
            <p>
              {{ getRelativeBirth(bovine.birthDate) }}
            </p>
          </span>
        </div>
        <div class="bovine-root">
          <span class="bovine-card-field">
            <span>
              <IconRoot />
            </span>
            <p>
              {{ bovine.bovineRoot ? bovine.bovineRoot.earring : '-' }}
            </p>
          </span>
        </div>
        <div class="status">
          <span class="bovine-card-field">
            <span class="material-icons">info</span>
            <p>{{ bovine?.status?.name || '-' }}</p>
          </span>
        </div>
        <div class="bull">
          <span class="bovine-card-field">
            <span>
              <IconBull />
            </span>
            <p>
              {{ bovine.bullType ? bovine.bullType.name : '-' }}
            </p>
          </span>
        </div>
        <div class="color">
          <span class="bovine-card-field">
            <span>
              <IconColor />
            </span>
            <p>
              {{ bovine.color || '-' }}
            </p>
          </span>
        </div>
        <div class="farm">
          <span class="bovine-card-field">
            <span class="material-icons">map</span>
            <p
              class="text-field-overflow"
              v-tooltip.top="bovine?.farm?.name || ''"
            >
              {{ bovine?.farm?.name || '-' }}
            </p>
          </span>
        </div>
        <div class="farm-lot">
          <span class="bovine-card-field">
            <span class="material-icons">place</span>
            <p>{{ bovine?.farmLot?.name || '-' }}</p>
          </span>
        </div>
        <div class="owner">
          <span class="bovine-card-field">
            <span class="material-icons">person</span>
            <p
              class="text-field-overflow"
              v-tooltip.top="bovine?.owner?.name || ''"
            >
              {{ bovine?.owner?.name || '-' }}
            </p>
          </span>
        </div>
        <div class="observations">
          <span class="bovine-card-field">
            <span class="material-icons">notes</span>
            <p
              class="text-field-overflow"
              v-tooltip.top="getObservationTooltip(bovine?.observation)"
            >
              {{ getTruncatedObservation(bovine?.observation) }}
            </p>
          </span>
        </div>
        <div class="child-birth" v-if="isTypeBovineRoot(bovine.typeId)">
          <span class="bovine-card-field">
            <BovineChildBirth
              :bovine="bovine"
              :inactive="this.isInactive"
            ></BovineChildBirth>
          </span>
        </div>
        <div class="tags">
          <span
            class="bovine-tag"
            v-for="(tag, index) in bovine.tags"
            :key="index"
          >
            <AppTag :text="tag.tag.name"></AppTag>
          </span>
        </div>
        <div class="calves">
          <span class="bovine-card-field">
            <BovineCalvesTags
              v-if="!this.isInactive"
              :bovine="bovine"
            ></BovineCalvesTags>
          </span>
        </div>
      </div>
    </template>
  </Card>
</template>

<script>
import BovineTag from '../../../components/BovineTag.vue';
import IconBreed from '../../../components/icon/IconBreed.vue';
import IconWeight from '../../../components/icon/IconWeight.vue';
import IconBull from '../../../components/icon/IconBull.vue';
import IconRoot from '../../../components/icon/IconRoot.vue';
import IconColor from '../../../components/icon/IconColor.vue';
import BovineChildBirth from './BovineChildBirth.vue';
import { isTypeBovineRoot } from '../../../lib/bovines';
import BovineCalvesTags from './BovineCalvesTags.vue';

export default {
  name: 'BovineCard',
  components: {
    BovineTag,
    IconBreed,
    IconWeight,
    IconRoot,
    IconBull,
    IconColor,
    BovineChildBirth,
    BovineCalvesTags,
  },
  props: {
    bovine: Object,
  },
  computed: {
    isInactive() {
      return this.bovine?.status && !this.bovine?.status?.available;
    },
    cardStyle() {
      if (this.isInactive) {
        return { backgroundColor: 'rgba(229, 229, 229, 0.5)' };
      }
      return {};
    },
  },
  methods: {
    isTypeBovineRoot,
    getRelativeBirth(birthDate) {
      if (!birthDate) {
        return '-';
      }
      const yearDiff = this.$dayjs().diff(birthDate, 'y');
      const monthDiff = this.$dayjs().diff(birthDate, 'M') - yearDiff * 12;
      const dayDiff = this.$dayjs().diff(birthDate, 'd');
      const year = yearDiff > 1 ? `${yearDiff} anos` : `${yearDiff} ano`;
      const month = monthDiff > 1 ? `${monthDiff} meses` : `${monthDiff} mês`;
      const day = dayDiff > 1 ? `${dayDiff} dias` : `${dayDiff} dia`;
      if (!monthDiff && !yearDiff) {
        return day;
      }
      if (!yearDiff) {
        return month;
      }
      if (!monthDiff) {
        return year;
      }
      return `${year} e ${month}`;
    },
    getTruncatedObservation(observation) {
      if (!observation) {
        return '-';
      }
      const maxLength = 50;
      if (observation.length > maxLength) {
        return `${observation.substring(0, maxLength)}...`;
      }
      return observation;
    },
    getObservationTooltip(observation) {
      if (!observation) {
        return '';
      }
      const maxLength = 50;
      if (observation.length > maxLength) {
        return observation;
      }
      return '';
    },
  },
};
</script>

<style scoped>
/* grid style */
.grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(7, 1fr) min-content min-content min-content;
  gap: 1em 0.2em !important;
  grid-template-areas:
    'id-brinco breed'
    'weight birth-date'
    'age bovine-root'
    'status bull'
    'color child-birth'
    'farm farm'
    'farm-lot farm-lot'
    'owner owner'
    'calves calves'
    'tags tags'
    'observations observations';
}
.grid-tags {
  grid-template-areas:
    'id-brinco breed'
    'weight birth-date'
    'age bovine-root'
    'status bull'
    'color color'
    'farm farm'
    'farm-lot farm-lot'
    'owner owner'
    'calves calves'
    'tags tags'
    'observations observations';
}

@media (min-width: 600px) {
  .grid-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(8, 1fr);
    gap: 1em 0.2em !important;
    grid-template-areas:
      'id-brinco breed'
      'weight birth-date'
      'age bovine-root'
      'status bull'
      'color child-birth'
      'farm farm-lot'
      'owner calves'
      'tags tags'
      'observations observations';
  }
  .grid-tags {
    grid-template-areas:
      'id-brinco breed'
      'weight birth-date'
      'age bovine-root'
      'status bull'
      'color color'
      'farm farm-lot'
      'owner calves'
      'tags tags'
      'observations observations';
  }
}

@media (min-width: 960px) {
  .grid-container {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1.3fr 1fr;
    grid-template-rows: 1fr 1fr 1fr max-content;
    gap: 0.5em 1em;
    grid-template-areas:
      'id-brinco birth-date status farm child-birth'
      'breed age bull farm-lot tags'
      'weight bovine-root color owner calves'
      'observations observations observations observations observations';
  }
  .grid-tags {
    grid-template-areas:
      'id-brinco birth-date status farm tags'
      'breed age bull farm-lot tags'
      'weight bovine-root color owner calves'
      'observations observations observations observations observations';
  }
}

.id-brinco {
  grid-area: id-brinco;
}

.breed {
  grid-area: breed;
}

.weight {
  grid-area: weight;
}

.birth-date {
  grid-area: birth-date;
}

.age {
  grid-area: age;
}

.bovine-root {
  grid-area: bovine-root;
}

.status {
  grid-area: status;
}

.bull {
  grid-area: bull;
}

.color {
  grid-area: color;
}

.farm {
  grid-area: farm;
}

.farm-lot {
  grid-area: farm-lot;
}

.owner {
  grid-area: owner;
}

.observations {
  grid-area: observations;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.observations .bovine-card-field {
  max-width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
}

.observations .bovine-card-field p {
  max-width: 100%;
  overflow: hidden;
  min-width: 0;
}

.child-birth {
  grid-area: child-birth;
}

.tags {
  grid-area: tags;
}

.calves {
  grid-area: calves;
}

/* content style */
#bovine-card >>> .p-card-content {
  padding: 0 !important;
}

.bovine-card-field p {
  display: inline-block;
  vertical-align: top;
  padding-top: 2px;
}

.bovine-card-field span {
  padding-right: 10px;
}

.bovine-tag {
  padding: 0 5px 5px 0;
  height: fit-content;
}

.tags {
  display: flex;
  flex-flow: row wrap;
  align-content: flex-start;
}
</style>
